/* import __COLOCATED_TEMPLATE__ from './create.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';

/**
 *
 * Create form for a new project.
 *
 *
 * ```
 * <Page::Projects::Create
 *   @organization={{this.model.organization}}
 *   @projects={{array project1 project2}}
 * />
 * ```
 *
 * @class PageProjectsCreate
 *
 */

export const MIN_PROJECT_NAME_LENGTH = 3;

/**
 * The current organization's projects list.
 * @argument projects
 * @type {Array}
 */

export default class PageProjectsCreate extends Component {
  @service api;
  @service router;
  @service('resources.project') projects;
  @service intl;
  @service flashMessages;

  @tracked name = '';
  @tracked nameIsPristine = true;
  @tracked description = '';
  @tracked validationError;

  @task
  *save(evt) {
    // don't submit the form
    evt.preventDefault();

    this.nameIsPristine = false;

    yield this.validate.perform();

    if (this.validationError) {
      return;
    }
    const { organization } = this.args;

    const { name, description } = this;
    const project = yield this.projects.createProject.perform(
      name,
      description,
      organization
    );

    this.onProjectCreateSuccess(project);
  }

  @task
  *validate() {
    const { name } = this;
    const noSpecialCharactersRegex = /[^a-zA-Z0-9\s_\-.]/;

    try {
      const validationError = new Error();
      validationError.details = [];

      // Check that project name is not blank
      if (!name || !name.length) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-cannot-be-blank-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name is greater than the set minimum length
      if (name && name.length < MIN_PROJECT_NAME_LENGTH) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-must-be-minimum-length-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not contain disallowed special characters
      if (name && noSpecialCharactersRegex.test(name)) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-contains-disallowed-characters-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not begin with a space
      if (name && name.startsWith(' ')) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-begins-with-space-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not end with a space
      if (name && name.endsWith(' ')) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-ends-with-space-error'
                ),
              },
            ],
          },
        ];
      }

      if (validationError.details.length) {
        throw validationError;
      } else {
        yield (this.validationError = undefined);
      }
    } catch (e) {
      this.validationError = e;
    }
  }

  onProjectCreateSuccess(project) {
    this.flashMessages.success(
      this.intl.t('components.page.projects.create.project-created'),
      {
        content: this.intl.t(
          'components.page.projects.create.project-has-been-created',
          {
            projectName: project.name,
          }
        ),
        actionText: this.intl.t(
          'components.page.projects.create.go-to-project'
        ),
        onAction: () => {
          return this.router.transitionTo(
            'cloud.orgs.detail.projects.detail',
            project.id
          );
        },
      }
    );
    this.router.transitionTo('cloud.orgs.detail.projects.list');
  }
}
